import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getGeneratedDocuments, deleteGeneratedDocument, updateGeneratedDocument } from '../utils/storage';
import type { SavedDocument, IconName } from '../types';
import Icon from '../components/Icon';
import { DOCUMENT_TYPES } from '../constants';

const MyPagesPage: React.FC = () => {
  const [documents, setDocuments] = useState<SavedDocument[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    setDocuments(getGeneratedDocuments());
  }, []);

  const filteredDocuments = useMemo(() => {
    if (!searchTerm) {
      return documents;
    }
    return documents.filter(doc =>
      doc.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, documents]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = (docId: string) => {
    if (window.confirm('هل أنت متأكد من رغبتك في حذف هذه الصفحة؟ لا يمكن التراجع عن هذا الإجراء.')) {
      deleteGeneratedDocument(docId);
      setDocuments(getGeneratedDocuments());
    }
  };
  
  const handleContentChange = (docId: string, newContent: string) => {
    setDocuments(prevDocs => 
        prevDocs.map(doc => 
            doc.id === docId ? { ...doc, content: newContent } : doc
        )
    );
  };
  
  const handleSave = (docId: string, content: string) => {
    updateGeneratedDocument(docId, content);
    setSavedId(docId);
    setTimeout(() => setSavedId(null), 2000);
  };

  const handleCopy = (content: string, docId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(docId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpand = (docId: string) => {
    setExpandedId(expandedId === docId ? null : docId);
  };
  
  const getDocumentIcon = (docTypeId: string): IconName => {
    const docType = DOCUMENT_TYPES.find(d => d.id === docTypeId);
    return docType ? docType.iconName : 'FileText';
  };

  if (documents.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-[70px] py-16 text-center">
        <Icon name="File" className="w-24 h-24 mx-auto text-[var(--color-text-body)]/50 mb-6" />
        <h1 className="text-3xl font-bold text-[var(--color-text-heading)]">لا توجد صفحات محفوظة</h1>
        <p className="mt-4 text-lg text-[var(--color-text-body)]">
          لم تقم بإنشاء أي صفحات بعد. ابدأ بإنشاء صفحتك الأولى.
        </p>
        <Link
          to="/"
          className="mt-8 inline-block bg-[var(--color-primary)] text-[var(--color-white)] font-bold py-3 px-8 rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors duration-200 text-lg"
        >
          العودة إلى القوالب
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-[70px] py-12">
      <header className="mb-10">
        <h1 className="text-4xl font-extrabold text-[var(--color-text-heading)]">صفحاتي المحفوظة</h1>
        <p className="mt-3 text-lg text-[var(--color-text-body)] max-w-3xl">
          هنا تجد جميع الصفحات التي قمت بإنشائها. يتم حفظها في متصفحك.
        </p>
      </header>
        
      <div className="relative mb-8 max-w-xl">
        <input
          type="text"
          placeholder="ابحث في صفحاتك..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full pl-12 pr-4 py-3 border-2 border-[var(--color-border)] rounded-full focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] transition-shadow bg-[var(--color-input-bg)] text-[var(--color-text-body)] placeholder:text-[var(--color-text-body)]"
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-4 text-[var(--color-text-body)] pointer-events-none">
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </div>
      </div>

      <main className="space-y-4">
        {filteredDocuments.length > 0 ? (
            filteredDocuments.map((doc) => {
            const isExpanded = expandedId === doc.id;
            const isCopied = copiedId === doc.id;
            const isSaved = savedId === doc.id;
            const docIconName = getDocumentIcon(doc.documentTypeId);

            return (
              <div key={doc.id} className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] overflow-hidden">
                <button
                  onClick={() => toggleExpand(doc.id)}
                  className="w-full flex items-center justify-between p-5 text-right focus:outline-none hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-center gap-4">
                    <Icon name={docIconName} className="w-6 h-6 text-[var(--color-primary)]" />
                    <div>
                      <h2 className="text-lg font-semibold text-[var(--color-text-heading)]">{doc.title}</h2>
                      <p className="text-sm text-[var(--color-text-body)]">
                        تم الإنشاء في: {new Date(doc.createdAt).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <Icon name="ChevronDown" className={`w-6 h-6 text-[var(--color-text-body)] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {isExpanded && (
                  <div className="p-5 border-t border-[var(--color-border)] bg-black/5 dark:bg-white/5">
                    <textarea
                      value={doc.content}
                      onChange={(e) => handleContentChange(doc.id, e.target.value)}
                      className="w-full h-64 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-md p-3 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] resize-none text-[var(--color-text-body)] mb-4"
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => handleCopy(doc.content, doc.id)}
                        className={`w-full sm:w-auto flex-grow flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-bold transition-colors ${
                          isCopied
                            ? 'bg-[var(--color-success)] text-[var(--color-white)]'
                            : 'bg-[var(--color-text-heading)] text-[var(--color-bg-start)] hover:bg-opacity-90'
                        }`}
                      >
                        {isCopied ? <Icon name="Check" className="w-5 h-5" /> : <Icon name="Copy" className="w-5 h-5" />}
                        {isCopied ? 'تم النسخ!' : 'نسخ المحتوى'}
                      </button>
                      <button
                        onClick={() => handleSave(doc.id, doc.content)}
                        className={`w-full sm:w-auto flex-grow flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-bold transition-colors ${
                            isSaved
                            ? 'bg-[var(--color-success)] text-[var(--color-white)]'
                            : 'bg-[var(--color-primary)] text-[var(--color-white)] hover:bg-[var(--color-primary-hover)]'
                        }`}
                        >
                        {isSaved ? <Icon name="Check" className="w-5 h-5" /> : <Icon name="Save" className="w-5 h-5" />}
                        {isSaved ? 'تم الحفظ!' : 'حفظ التغييرات'}
                       </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-bold bg-[var(--color-danger)] text-[var(--color-white)] hover:bg-opacity-90 transition-colors"
                      >
                        <Icon name="Trash2" className="w-5 h-5" />
                        حذف
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
            })
        ) : (
             <div className="text-center py-16 bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)]">
                <p className="text-xl text-[var(--color-text-body)]">لم يتم العثور على نتائج. حاول البحث بكلمة أخرى.</p>
            </div>
        )}
      </main>
    </div>
  );
};

export default MyPagesPage;